import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, CircularProgress, Skeleton, TablePagination,
  Snackbar, Alert, InputAdornment, Tooltip, Avatar, Paper, Grid
} from "@mui/material";
import { Plus, Pencil, Trash2, Layers, Tag, Upload, Hash, Link, Search, X, AlignLeft  } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetServiceCategories,
  adminGetServiceCategoryById,
  adminCreateServiceCategory,
  adminUpdateServiceCategory,
  adminDeleteServiceCategory,
} from "../../api/services";

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];
const STATUS_STYLES = {
  ACTIVE:   { bg: "rgba(34,197,94,0.1)",  color: "#16A34A" },
  DRAFT:    { bg: "rgba(245,158,11,0.1)", color: "#B45309" },
  INACTIVE: { bg: "rgba(239,68,68,0.1)",  color: "#DC2626" },
};

const slugify = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const DEFAULT_VALUES = {
  name: "",
  slug: "",
  description: "",
  icon: "",
  order: 0,
  status: "DRAFT",
};

export default function ServiceCategoriesAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { register, handleSubmit, reset, watch, control } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const watchName = watch("name", "");
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Fetch categories
  const { data: apiCategories = [], isLoading } = useQuery({
    queryKey: ["admin-service-categories"],
    queryFn: () => adminGetServiceCategories().then(r => r.data),
  });

  const categories = Array.isArray(apiCategories) ? apiCategories : [];
  const filteredCategories = categories.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const paginatedCategories = filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalCount = filteredCategories.length;

  const createMutation = useMutation({
    mutationFn: (data) => adminCreateServiceCategory(data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-service-categories"]);
      notify("Category created");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create category", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateServiceCategory(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-service-categories"]);
      notify("Category updated");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update category", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteServiceCategory,
    onSuccess: () => {
      qc.invalidateQueries(["admin-service-categories"]);
      notify("Category deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(err?.response?.data?.message || "Couldn't delete category", "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

 const onSubmit = (values) => {
  const payload = {
    name: values.name,
    slug: values.slug?.trim() || slugify(values.name),
    description: values.description,
    icon: values.icon,  // <-- Icon field IS included here
    order: values.order ?? 0,
    status: values.status,
  };

  if (editing) updateMutation.mutate({ id: editing.id, data: payload });
  else createMutation.mutate(payload);
};

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = async (c) => {
    setEditLoading(true);
    try {
      const { data } = await adminGetServiceCategoryById(c.id);
      setEditing(data);
      reset({
        name: data.name ?? "",
        slug: data.slug ?? "",
        description: data.description ?? "",
        icon: data.icon ?? "",
        order: data.order ?? 0,
        status: data.status ?? "DRAFT",
      });
      setOpen(true);
    } catch (err) {
      notify(err?.response?.data?.message || "Couldn't load category details", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    reset(DEFAULT_VALUES);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  return (
    <AdminShell title="Service Categories">
      {/* Page header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5, mb: 2.5,
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Manage service categories. These are used to group services on the public site.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Category
        </Button>
      </Box>

      {/* Search Bar */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 2, mb: 2.5,
      }}>
        <TextField
          fullWidth
          placeholder="Search categories by name or description..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94A3B8" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              border: "1px solid rgba(15,23,42,0.15)",
              "&:hover": { borderColor: "#2563EB" },
            },
          }}
        />
      </Card>

      {/* Table Card */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden"
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Category", "Description", "Order", "Status", "Services", ""].map(h => (
                  <TableCell key={h} sx={{
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.03em",
                    textTransform: "uppercase", color: "text.secondary",
                    borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap"
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 1 ? 200 : 80} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: "14px",
                      bgcolor: "rgba(124,58,237,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 1.5
                    }}>
                      <Layers size={22} color="#7C3AED" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      No categories found
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Add your first category to organize services."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedCategories.map((c) => {
                const statusStyle = STATUS_STYLES[c.status] || STATUS_STYLES.DRAFT;
                return (
                  <TableRow key={c.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 38, height: 38, borderRadius: "9px",
                            bgcolor: "rgba(124,58,237,0.1)", color: "#7C3AED", flexShrink: 0
                          }}
                        >
                          <Layers size={16} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                            {c.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary" }}>
                      {c.description || "—"}
                    </TableCell>

                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {c.order ?? "—"}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={c.status || "DRAFT"}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                      />
                    </TableCell>

                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {c.service_count ?? 0}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEdit(c)}
                              disabled={editLoading}
                              sx={{
                                border: "1px solid rgba(15,23,42,0.1)",
                                borderRadius: "8px",
                                "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                              }}
                            >
                              {editLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(c)}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid rgba(15,23,42,0.08)",
            "& .MuiTablePagination-toolbar": { padding: "16px 24px" },
            "& .MuiTablePagination-selectLabel": {
              fontSize: "13.5px", color: "text.secondary", marginRight: "8px",
            },
            "& .MuiTablePagination-displayedRows": { fontSize: "13.5px", color: "text.secondary" },
            "& .MuiInputBase-root": {
              borderRadius: "8px", border: "1px solid rgba(15,23,42,0.15)",
            },
            "& .MuiTablePagination-actions": { gap: "4px" },
            "& .MuiIconButton-root": {
              borderRadius: "8px", border: "1px solid rgba(15,23,42,0.1)", width: "32px", height: "32px",
            },
          }}
        />
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 30px 70px rgba(15,23,42,0.22)",
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2.5,
          px: 3,
          background: "linear-gradient(135deg, #7C3AED 0%, #2563EB 55%, #1D4ED8 100%)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <Layers size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {editing ? "Edit Category" : "Add New Category"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", fontWeight: 500, mt: 0.25 }}>
                {editing ? `Editing: ${editing.name}` : "Create a new service category"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: 3,
            pb: 1,
            px: 3,
            maxHeight: "65vh",
            overflowY: "auto",
            backgroundColor: "#F1F5F9",
          }}>
            {/* Basic Information */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #7C3AED",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(124,58,237,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Layers size={17} color="#7C3AED" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Basic Information
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Category Name"
                    fullWidth
                    disabled={saving}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("name", { required: true })}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Slug"
                    fullWidth
                    disabled={saving}
                    placeholder={watchName ? slugify(watchName) : "auto-generated from name"}
                    helperText="Leave blank to auto-generate. Lowercase letters, numbers, hyphens only."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Link size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("slug")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={saving}
                    placeholder="Brief description of this category"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
                        <AlignLeft size={16} color="#94A3B8" />
                      </InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("description")}
                  />
                </Box>
              </Box>
            </Paper>

         {/* Display Settings */}
<Paper elevation={0} sx={{
  p: 3,
  borderRadius: "16px",
  border: "1px solid rgba(15,23,42,0.06)",
  borderLeft: "4px solid #EA580C",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
}}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
    <Box sx={{
      width: 32, height: 32, borderRadius: "9px",
      backgroundColor: "rgba(234,88,12,0.1)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Tag size={17} color="#EA580C" />
    </Box>
    <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
      Display Settings
    </Typography>
  </Box>

  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
    <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
      <TextField
        label="Icon"
        fullWidth
        disabled={saving}
        placeholder="e.g. cloud, shield, code (icon name or URL)"
        InputProps={{
          startAdornment: <InputAdornment position="start"><Link size={16} color="#94A3B8" /></InputAdornment>
        }}
        InputLabelProps={{ shrink: true }}
        {...register("icon")}
      />
    </Box>

    <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
      <TextField
        label="Display Order"
        type="number"
        fullWidth
        disabled={saving}
        placeholder="0 = first"
        inputProps={{ min: 0, step: 1 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><Hash size={16} color="#94A3B8" /></InputAdornment>
        }}
        InputLabelProps={{ shrink: true }}
        {...register("order", { min: 0 })}
      />
    </Box>

    <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <TextField
            select
            label="Status"
            fullWidth
            disabled={saving}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>
            }}
            InputLabelProps={{ shrink: true }}
            {...field}
            value={field.value ?? ""}
          >
            {STATUSES.map(s => (
              <MenuItem key={s} value={s}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: "50%",
                    bgcolor: STATUS_STYLES[s]?.bg || "rgba(15,23,42,0.08)"
                  }} />
                  {s}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Box>
  </Box>
</Paper>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "#FFFFFF",
            display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
          }}>
            <Button
              onClick={closeDialog}
              disabled={saving}
              sx={{
                textTransform: "none", fontWeight: 700, color: "#475569",
                px: 2.5, py: 1, borderRadius: "10px",
                backgroundColor: "rgba(15,23,42,0.04)",
                "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)",
                px: 3.5, py: 1.1, minWidth: 140,
                boxShadow: "0 4px 14px rgba(124,58,237,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #6D28D9 0%, #1D4ED8 100%)",
                  boxShadow: "0 6px 18px rgba(124,58,237,0.4)"
                },
              }}
            >
              {saving ? "Saving..." : editing ? "Update Category" : "Create Category"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this category?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            All services in "{confirmDelete?.name}" will become uncategorized. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          px: 3, pb: 2.5,
          borderTop: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}